import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { formatMoney } from "@/lib/formatters"
import type { StatusTone } from "@/lib/status-colors"
import { TillFigure } from "@/features/staff/till-figure"
import { ReportFigures, ReportList, ReportRow } from "@/features/staff/reports/report-block"
import { formatShare, tReports, tSharedReports } from "@/features/staff/reports/format"
import type { AbcClass, MixItem, ProductMix } from "@/features/staff/reports/types"

/**
 * R2 — what sold, and which handful of dishes carries the menu.
 *
 * **Its money is not a headline and never will be.** This block counts dishes;
 * R1 counts payments. A split share carries a subtotal that belongs to no dish,
 * and tips and diner fees belong to no dish at all, so the two cannot be made to
 * agree without lying about one of them. The amount column is therefore labelled
 * "valor de los platillos", rendered at the same weight as every other row
 * figure, and the card says outright that it will not match the total above.
 *
 * The ABC classes are tones, not verdicts. `available / reserved / disabled` is a
 * three-step ramp with no moral content: a C is the tail of the menu, not a
 * failure, and colouring it `destructive` would tell an owner to delete dishes
 * that pay the rent on a slow Tuesday.
 *
 * **The cost column is here and nowhere else on this screen.** `#313` cut margin
 * per dish because no cost existed; it does now, frozen onto the order when the
 * kitchen accepted the ticket. It sits beside the dish it belongs to rather than
 * anywhere near the sales headline, which already has four money bases arguing
 * with each other.
 *
 * A dish with no recipe has NO cost, so it has no margin either, and it says so
 * in words. Rendering that as `$0.00` would report free food at 100% margin —
 * the single most misleading figure this block could carry — and the server
 * sends `null` precisely so this file cannot accidentally print a zero.
 */
const ABC_TONE: Record<AbcClass, StatusTone> = {
  a: "available",
  b: "reserved",
  c: "disabled",
}

/** How many rows to show before the tail is folded away. */
const HEADLINE_CLASSES: AbcClass[] = ["a", "b"]

/**
 * What the row says about its own margin.
 *
 * A dish with no recipe gets a sentence, not a dash: "sin receta, todavía no hay
 * costo" tells the manager what to do about it, where "—" in a column of pesos
 * reads as a rendering bug. Either way no number is invented.
 */
function marginNote(item: MixItem): string {
  return item.margin === null
    ? tReports("mix_item_margin_unknown")
    : tReports("mix_item_margin", { margin: formatMoney(item.margin) })
}

export function ReportMixBlock({ mix }: { mix: ProductMix }) {
  const [showAll, setShowAll] = useState(false)

  if (mix.itemCount === 0) return null

  // One predicate for the whole column. It is false for a restaurant that does
  // not buy through Menny and false for one whose dishes have no recipes yet —
  // two different absences that both mean "there is no cost to show", and a
  // block that distinguished them would be answering a question nobody asked
  // while reading last month's sales.
  const costed = mix.costedQuantity > 0

  // A and B are, by construction, the dishes that make 95% of the revenue —
  // usually a short list, which is the entire point of running ABC. The tail is
  // folded rather than truncated: the count below is a count of rows, and no
  // amount is summed here to produce it.
  const headline = mix.items.filter((item) => HEADLINE_CLASSES.includes(item.abcClass))
  const visible = showAll || headline.length === 0 ? mix.items : headline
  const hidden = mix.items.length - visible.length

  return (
    <Card density="compact" data-testid="report-mix">
      <CardHeader className="border-b">
        <CardTitle>{tReports("mix_title")}</CardTitle>
        <CardDescription>{tSharedReports("notes.mix_basis")}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <ReportFigures>
          <TillFigure
            label={tReports("mix_items_label")}
            value={String(mix.itemCount)}
            testId="report-mix-items"
          />
          <TillFigure
            label={tReports("mix_quantity_label")}
            value={String(mix.quantity)}
            testId="report-mix-quantity"
          />
          <TillFigure
            label={tReports("mix_value_label")}
            value={formatMoney(mix.net)}
            hint={tReports("mix_value_hint")}
            testId="report-mix-value"
          />
          {costed && mix.margin !== null ? (
            <TillFigure
              label={tReports("mix_margin_label")}
              value={formatMoney(mix.margin)}
              hint={
                mix.foodCostRatio === null
                  ? undefined
                  : tReports("mix_margin_hint", { ratio: formatShare(mix.foodCostRatio) })
              }
              testId="report-mix-margin"
            />
          ) : null}
        </ReportFigures>

        <ReportList testId="report-mix-items-list">
          {visible.map((item) => (
            <ReportRow
              key={item.locationItemId ?? item.name}
              testId={`report-mix-item-${item.locationItemId ?? "unknown"}`}
              tone={ABC_TONE[item.abcClass]}
              label={item.name}
              sublabel={[
                tReports("mix_item_meta", {
                  abc: tReports(`mix_class_${item.abcClass}`),
                  share: formatShare(item.share),
                }),
                costed ? marginNote(item) : null,
              ]
                .filter(Boolean)
                .join(" · ")}
              meta={tReports("mix_quantity_count", { count: item.quantity })}
              amount={formatMoney(item.net)}
            />
          ))}
        </ReportList>

        {hidden > 0 ? (
          <Button
            variant="secondary"
            className="w-full"
            data-testid="report-mix-show-all"
            onClick={() => setShowAll(true)}
          >
            {tReports("mix_show_all", { count: hidden })}
          </Button>
        ) : null}

        {/* How much of the mix the margin above is built on. The server already
            computes that margin over the costed lines alone, so this is not a
            disclaimer on a mixed figure — it is the scope of an honest one, and
            a period that is a third costed says so instead of implying it
            speaks for the whole menu. */}
        {costed ? (
          <p className="text-xs text-muted-foreground" data-testid="report-mix-cost-coverage">
            {tReports("mix_cost_coverage", { costed: mix.costedQuantity, total: mix.quantity })}
          </p>
        ) : null}

        {/* R8 — Rappi, UberEats and every other external channel materializes no
            order line, so none of it is in here. An owner reading a mix that is
            wrong by that entire share, with nothing on screen saying so, would
            re-plan a menu around it. */}
        <p className="text-xs text-muted-foreground" data-testid="report-mix-coverage">
          {tSharedReports("notes.external_channels")}
        </p>
      </CardContent>
    </Card>
  )
}
